import * as S from "@/containers/Project/components/styled";
import { addHistoryToData } from "@/containers/Project/helper";
import { useProjectContext } from "@/providers";
import {
  useGetProjectTemplateQuery,
  useGetScheduleHistoryAsOfDateQuery,
} from "@/services";
import dayjs from "dayjs";
import { bool, func, number, object, string } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

export const SingleScheduleTable = ({ projectId, schedule, hideRows }) => {
  const intl = useIntl();
  const { project } = useProjectContext();

  const [tableData, setTableData] = useState(schedule.data);

  // grouped state props to one object to reduce duplicate useEffect dependency triggers
  const [dateSelectorInfo, setDateSelectorInfo] = useState({
    asOfDate: null,
    baselineDates: null,
    hasPastReviewDate: false,
    loaded: false,
  });

  const { isError: isScheduleHistoryError, data: scheduleHistoryAsOfDate } =
    useGetScheduleHistoryAsOfDateQuery(
      {
        projectId,
        scheduleId: schedule?.id,
        asOfDate: dateSelectorInfo.asOfDate?.toISOString(),
      },
      {
        skip: !dateSelectorInfo.asOfDate || !(schedule?.col?.length > 1),
      }
    );
  const { data: projectTemplate } = useGetProjectTemplateQuery(
    project.projectTemplateId,
    {
      skip: !project?.projectTemplateId,
    }
  );

  useEffect(() => {
    if (!scheduleHistoryAsOfDate && dateSelectorInfo.hasPastReviewDate) return;

    if (intl && schedule && dateSelectorInfo.loaded) {
      const newData = addHistoryToData(
        scheduleHistoryAsOfDate?.milestoneDatesSnapshotAsOfDate,
        dateSelectorInfo.asOfDate,
        dateSelectorInfo.baselineDates,
        (input) =>
          setDateSelectorInfo({ ...dateSelectorInfo, asOfDate: input }),
        schedule,
        intl.formatMessage({ id: "projectMilestoneTable.actualDate" }),
        intl.formatMessage({ id: "projectMilestoneTable.targetDate" }),
        dateSelectorInfo.hasPastReviewDate
      );

      setTableData(newData);
    }
  }, [intl, schedule, scheduleHistoryAsOfDate, dateSelectorInfo]);

  useEffect(() => {
    if (!isScheduleHistoryError) return;

    toast.error(intl.formatMessage({ id: "general.toastSomethingWentWrong" }));
  }, [intl, isScheduleHistoryError]);

  useEffect(() => {
    if (project && projectTemplate) {
      const datesInCategory = projectTemplate?.baselineDates?.filter(
        (baselineDate) =>
          baselineDate.productCategories.includes(project.productCategory) &&
          baselineDate.dates.length > 0
      );

      const updatedDateSelectorInfo = {
        asOfDate: null,
        baselineDates: null,
        hasPastReviewDate: false,
        loaded: false,
      };

      if (datesInCategory?.length > 0) {
        const baselinesConverted = datesInCategory
          .map((baselineDate) => baselineDate.dates)
          .flat()
          .map((date) => dayjs(date, "MM-DD-YYYY"));

        const sortedDates = baselinesConverted.sort((a, b) => a.diff(b));

        let targetDateIndex = 0;

        const now = dayjs().startOf("day");

        for (let i = sortedDates.length - 1; i >= 0; i--) {
          if (dayjs(sortedDates[i]).isBefore(now)) {
            targetDateIndex = i;
            break;
          }
        }

        const sortedDateStrings = sortedDates.map((date) =>
          dayjs(date, "MM/DD/YYYY")
        );

        updatedDateSelectorInfo.baselineDates = sortedDateStrings;
        updatedDateSelectorInfo.asOfDate = sortedDateStrings[targetDateIndex];
        updatedDateSelectorInfo.hasPastReviewDate = true;
      } else {
        updatedDateSelectorInfo.asOfDate = null;
      }

      updatedDateSelectorInfo.loaded = true;
      setDateSelectorInfo(updatedDateSelectorInfo);
    }
  }, [project, projectTemplate]);

  return (
    <S.AntMilestoneTable
      columns={schedule.col}
      dataSource={tableData}
      pagination={false}
      size="middle"
      key={schedule.id}
      $hideRows={hideRows}
    />
  );
};

SingleScheduleTable.propTypes = {
  projectId: string,
  schedule: object,
  colWidth: number,
  hideRows: bool,
  dateFormatter: func,
};
